import type { Customer } from '../types';
import { customerApi } from '../services/api';

export interface CustomerMatch {
  customer: Customer;
  /** 0-1 overall match score based on name similarity. */
  matchConfidence: number;
  /** Whether the phone number also matches. */
  phoneMatches: boolean;
  /** Human-readable reasons for the match. */
  matchReasons: string[];
}

export interface CustomerMatchResult {
  /**
   * A customer whose name AND phone both match the AI-extracted data.
   * When set, the caller should auto-select this customer without prompting.
   */
  perfectMatch: Customer | null;
  /**
   * Customers whose name matches (score >= 0.7) regardless of phone.
   * Sorted by score descending. Shown as a recommendation list.
   */
  nameMatches: CustomerMatch[];
}

/**
 * Three-tier customer matching for AI-extracted data:
 *
 * 1. **Perfect match** — name score >= 0.85 AND phone matches → auto-select
 * 2. **Name matches** — name score >= 0.7, phone may differ → show list
 * 3. **No match** — nothing returned
 */
class CustomerMatcher {
  /** Minimum name similarity to consider a "perfect" match (combined with phone). */
  private static readonly PERFECT_NAME_THRESHOLD = 0.85;
  /** Minimum name similarity to include in recommendation list. */
  private static readonly NAME_MATCH_THRESHOLD = 0.7;

  /**
   * Find existing customers matching the AI-extracted data.
   */
  static async findMatches(
    firstName: string,
    lastName: string,
    phone: string,
  ): Promise<CustomerMatchResult> {
    const response = await customerApi.getAll();
    const allCustomers: Customer[] = response.data;

    if (allCustomers.length === 0) {
      return { perfectMatch: null, nameMatches: [] };
    }

    const normPhone = CustomerMatcher.normalizePhone(phone);

    const scored: CustomerMatch[] = allCustomers.map((customer) => {
      const reasons: string[] = [];

      // --- Name scoring ---
      const firstSim = CustomerMatcher.stringSimilarity(
        firstName.toLowerCase(),
        (customer.first_name || '').toLowerCase(),
      );
      const lastSim = CustomerMatcher.stringSimilarity(
        lastName.toLowerCase(),
        (customer.last_name || '').toLowerCase(),
      );
      const nameScore = (firstSim + lastSim) / 2;

      if (nameScore >= 0.95) {
        reasons.push('name exact match');
      } else if (nameScore >= 0.85) {
        reasons.push('name close match');
      } else if (nameScore >= 0.7) {
        reasons.push('name similar');
      }

      // --- Phone scoring ---
      const custPhone = CustomerMatcher.normalizePhone(customer.phone || '');
      const phoneMatches = normPhone !== '' && custPhone !== '' && normPhone === custPhone;
      if (phoneMatches) {
        reasons.push('phone matches');
      }

      return {
        customer,
        matchConfidence: nameScore,
        phoneMatches,
        matchReasons: reasons,
      };
    });

    // Find perfect match: name >= 0.85 AND phone matches
    const perfectCandidates = scored.filter(
      (m) => m.matchConfidence >= CustomerMatcher.PERFECT_NAME_THRESHOLD && m.phoneMatches,
    );
    const perfectMatch = perfectCandidates.length > 0
      ? perfectCandidates.reduce((a, b) => (b.matchConfidence > a.matchConfidence ? b : a)).customer
      : null;

    // Find name matches: name >= 0.7 (regardless of phone), sorted by score desc
    const nameMatches = scored
      .filter((m) => m.matchConfidence >= CustomerMatcher.NAME_MATCH_THRESHOLD)
      .sort((a, b) => {
        // Phone matches first, then by name score
        if (a.phoneMatches !== b.phoneMatches) return a.phoneMatches ? -1 : 1;
        return b.matchConfidence - a.matchConfidence;
      });

    return { perfectMatch, nameMatches };
  }

  // ── Kept from original for backward compatibility ──

  /** Levenshtein-distance-based string similarity (0 = different, 1 = identical). */
  static stringSimilarity(str1: string, str2: string): number {
    if (str1 === str2) return 1;
    const maxLen = Math.max(str1.length, str2.length);
    if (maxLen === 0) return 1;
    return 1 - CustomerMatcher.levenshteinDistance(str1, str2) / maxLen;
  }

  /** Strip all non-digit characters from a phone string. */
  static normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  /** Classic DP Levenshtein distance. */
  static levenshteinDistance(str1: string, str2: string): number {
    const m = str1.length;
    const n = str2.length;
    const matrix: number[][] = [];

    for (let i = 0; i <= n; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= m; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[n][m];
  }
}

export default CustomerMatcher;
